import {
  onCleanup,
  createMemo,
  mergeProps,
  splitProps,
  createEffect,
  createSelector,
  untrack,
  batch,
  Show,
} from "solid-js";
import html from "solid-js/html";
import {
  createContext,
  type ICustomElement,
  provide,
  consume,
} from "component-register";
import {
  customShadowlessElement,
  toHyphenated,
} from "@brytdesigns/web-component-core/utils";
import { useProduct } from "@brytdesigns/shopify-product-info-components";
import { correctElementType } from "@brytdesigns/web-component-core";

type Props = {
  activeMediaId?: string;
};

type ContextProps = {
  element: HTMLElement & ICustomElement;
} & Props;

const ACTIVE_MEDIA_ID_ATTR = toHyphenated("activeMediaId");

const ProductMediaContext = createContext((props: ContextProps) => {
  const [privateProps, publicProps] = splitProps(props, ["element"]);
  const [context, methods] = useProduct(privateProps.element);

  function setActiveMedia(value: string | number) {
    const current = untrack(() => publicProps.activeMediaId);
    if (`${value}` === `${current}`) return;
    privateProps.element.setAttribute(ACTIVE_MEDIA_ID_ATTR, `${value}`);
  }

  const isActive = createSelector(() => `${publicProps.activeMediaId}`);

  const activeProductImageId = createMemo(() => {
    if (
      !(
        "featured_media" in context?.product.selected_or_first_available_variant
      )
    ) {
      if ("featured_media" in context?.product) {
        const featuredMedia = context?.product.featured_media as {
          id: string | number;
        };
        return featuredMedia.id;
      }
    } else if (
      "featured_media" in context?.product.selected_or_first_available_variant
    ) {
      const featuredMedia = context?.product.selected_or_first_available_variant
        .featured_media as {
          id: string | number;
        };
      return featuredMedia.id;
    }
  });

  createEffect(() => {
    const imageId = activeProductImageId();
    if (!imageId) return;
    setActiveMedia(imageId);
  });

  function updateActiveMedia(value: string | number) {
    batch(() => {
      value = `${value}`;
      setActiveMedia(value);
      const variant = context.product.variants.find(
        //@ts-ignore
        (v) => v?.featured_media?.id.toString() === value,
      );
      if (!variant) return;
      methods.variant.update(`${variant.id}`);
    });
  }

  return [
    () => publicProps.activeMediaId,
    {
      update: updateActiveMedia,
      isActive,
    },
  ];
});

export const useProductMedia = (element: ICustomElement & HTMLElement) => {
  const context = consume(ProductMediaContext, element);
  if (!context) throw new Error("No product-media element found!");
  return context;
};

customShadowlessElement(
  "product-media",
  { activeMediaId: "" },
  (props, { element }) => {
    if (!props.activeMediaId)
      return console.warn(
        "product-media: active-media-id attribute is required!",
      );
    provide(
      ProductMediaContext,
      mergeProps(props, { element }),
      element as any,
    );
  },
);

customShadowlessElement(
  "product-media-image",
  {},
  correctElementType((props, { element }) => {
    const [activeMediaId] = useProductMedia(element);
    const mediaTemplates = Array.from(
      element.querySelectorAll<HTMLTemplateElement>("template[media-id]"),
    );
    if (!mediaTemplates.length)
      return console.warn("product-media-image: No media templates found!");

    const activeTemplate = createMemo(() => {
      const mediaId = activeMediaId();
      const template = mediaTemplates.find(
        (t) => t.getAttribute("media-id") === mediaId,
      );
      if (!template) return null;
      return template;
    });

    element.replaceChildren(...[]);

    return html`
    <${Show} when=${activeTemplate}>
      ${() => Array.from(activeTemplate()?.content.cloneNode(true).childNodes || [])}
    <//>
  `;
  }),
);

customShadowlessElement(
  "product-media-thumbnail",
  { mediaId: "", isActive: false },
  correctElementType((props, { element }) => {
    if (!props.mediaId)
      return console.warn(
        "product-media-thumbnail: media-id attribute is required!",
      );
    const [_, methods] = useProductMedia(element);

    function setIsActive(value: boolean) {
      if (value === props.isActive) return;
      element.setAttribute(toHyphenated("isActive"), `${value}`);
    }

    createEffect(() => {
      const controller = new AbortController();
      const mediaId = props.mediaId;

      element.addEventListener(
        "click",
        () => {
          methods.update(mediaId);
        },
        { signal: controller.signal },
      );

      return onCleanup(() => controller.abort());
    });

    createEffect(() => {
      const mediaId = props.mediaId;
      const isActive = methods.isActive(`${mediaId}`);
      setIsActive(isActive);
    });
  }),
);
